/* oxlint-disable class-methods-use-this */
import { EMITTER_CONSTANTS, ERROR_ACTION, SOCKET } from '@/shared/config';
import _ from 'lodash';
import { io, type Socket } from 'socket.io-client';
import { generateToken } from '..';
import { auth } from '../auth';
import emitter from '../eventEmitter';

/**
 * Singleton socket.io client owning the portal's realtime connection, room membership and server event fan-out. Authenticates with a generated token, wires connect, disconnect and reconnect handlers, re-joins previously stored rooms after a reconnect, queues event and report room joins until the socket is up, and re-broadcasts server messages such as programsUpdated, maintenanceChanged, extendActiveSession and client kicks onto the app emitter.
 */
class SocketClient {
  socket: Socket | null = null;

  isConnected: any;

  rooms: any;

  queueECGEvent: any;

  queueNewEvent: any;

  queueReportUpdate: any;

  intervalJoinRoomEvent: any;

  queueRoomEvent: any;

  intervalJoinRoomReport: any;

  queueRoomReport: any;

  timeOutOverviewtUpdate: any;

  queueOverviewUpdate: any;

  constructor() {
    this.isConnected = false;
    this.rooms = [];

    this.queueECGEvent = [];
    this.queueNewEvent = [];
    this.queueReportUpdate = [];

    this.intervalJoinRoomEvent = null;
    this.queueRoomEvent = [];

    this.intervalJoinRoomReport = null;
    this.queueRoomReport = [];

    this.timeOutOverviewtUpdate = undefined;
    this.queueOverviewUpdate = [];
  }

  getAuth = async () => {
    const accessToken = await generateToken();
    return { accessToken };
  };

  getSocket = (): Socket | null => this.socket;

  connectToServer = async () => {
    try {
      if (!this.socket) {
        this.socket = io(SOCKET, {
          forceNew: true,
          transports: ['websocket'],
          auth: async (cb) => cb(await this.getAuth()),
        });
        this.socket.on('connect', this.connectListener);
      }
    } catch (error) {
      console.error('Failed to connect socket: ', error);
      // emitter.emit(EMITTER_CONSTANTS.SOCKET_CONNECTION_ERROR, {
      //   name: 'Socket connection error',
      //   message: error instanceof Error ? error.message : String(error),
      //   error,
      //   socketId: this.socket?.id,
      // });
      this.disconnectSocket({ shouldLogout: true });
    }
  };

  connectListener = () => {
    console.log('on connect');

    this.isConnected = true;

    emitter.emit(EMITTER_CONSTANTS.SOCKET_CONNECTED);

    this.socket?.emit('room', 'maintenance-room');

    this.socket?.io.off('reconnect');
    this.socket?.on('disconnect', this.disconnectListener);

    this.socket?.on('KickClientsByToken', this.kickClientsByTokenListener);
    this.socket?.on('KickClientsByUsername', this.kickClientsByUsernameListener);
    this.socket?.on('programsUpdated', this.programsUpdatedListener);
    this.socket?.on('extendActiveSession', this.extendActiveSessionListener);
    this.socket?.on('maintenanceChanged', this.maintenanceChangedListener);
  };

  disconnectListener = (error: any) => {
    console.log('on disconnect: ', error);

    // emitter.emit(EMITTER_CONSTANTS.SOCKET_CONNECTION_ERROR, {
    //   name: 'Socket disconnection error',
    //   message: error,
    //   error,
    //   socketId: this.socket?.id,
    // });

    this.isConnected = false;

    this.socket?.io.on('reconnect', this.reconnectListener);

    this.socket?.off('connect');
    this.socket?.off('disconnect');

    this.socket?.off('KickClientsByToken');
    this.socket?.off('KickClientsByUsername');
    this.socket?.off('isPreparingFullData');
    this.socket?.off('newEvent');
    this.socket?.off('multipleEventsClosed');
    this.socket?.off('facilityUpdated');
    this.socket?.off('bucketChanged');
    this.socket?.off('moreStudies');
    this.socket?.off('programsUpdated');
    this.socket?.off('groupUpdated');
    this.socket?.off('studyMoved');
    this.socket?.off('unreviewedReportUpdated');
    this.socket?.off('extendActiveSession');
    this.socket?.off('maintenanceChanged');

    if (error && error.trim() === 'io server disconnect') {
      this.disconnectSocket({ shouldExpireToken: true });
    }
  };

  reconnectListener = () => {
    console.log('on reconnect');
    this.socket?.on('connect', this.connectListener);
    const uniqRooms = _.uniqWith(this.rooms, _.isEqual);
    emitter.emit(EMITTER_CONSTANTS.SOCKET_RECONNECTED);
    _.forEach(uniqRooms, (room) => {
      const { name, id } = room;
      switch (name) {
        case 'joinReportRoom': {
          this.sendJoinReportRoom(id);
          break;
        }
        case 'joinEventRoom': {
          this.sendJoinEventRoom(id);
          break;
        }
        default: {
          this.emitIntervalEvent(name, id);
          break;
        }
      }
    });
  };

  programsUpdatedListener = (msg: any) => {
    console.log('socket io - programsUpdatedListener', msg);
    emitter.emit(EMITTER_CONSTANTS.PROGRAMS_UPDATED, msg);
  };

  kickClientsByTokenListener = (msg: any) => {
    console.log('socket io - kickClientsByTokenListener', msg);
    emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.LOGOUT);
  };

  kickClientsByUsernameListener = (msg: any) => {
    console.log('socket io - kickClientsByUsernameListener', msg);
    emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.LOGOUT);
  };

  extendActiveSessionListener = (msg: any) => {
    console.log('socket io - extendActiveSessionListener', msg);
    emitter.emit(EMITTER_CONSTANTS.EXTEND_ACTIVE_SESSION, msg);
  };

  maintenanceChangedListener = (msg: any) => {
    console.log('socket io - maintenanceChangedListener', msg);
    emitter.emit(EMITTER_CONSTANTS.MAINTENANCE_CHANGED, msg);
  };

  emitIntervalEvent = (status: any, id: any) => {
    if (this.isConnected) {
      this.socket?.emit(status, id);
      this.handleStoreRooms(status, id);
    } else {
      /**
       * A polling timer that retries emitting a room event until the socket reports connected, then stores the room and clears itself. Also clears itself when no authenticated user id remains, guarding against emitting after logout.
       *
       * @returns The setInterval timer handle.
       */
      const interval = setInterval(() => {
        if (this.isConnected) {
          this.socket?.emit(status, id);
          this.handleStoreRooms(status, id);
          clearInterval(interval);
        }
        if (!auth.userId()) {
          clearInterval(interval);
        }
      }, 1000);
    }
  };

  handleStoreRooms = (name: any, id: any) => {
    if (!_.some(this.rooms, (x) => x.id === id)) {
      this.rooms.push({ name, id });
    }
  };

  emitRoom = (id: any) => {
    if (id && id !== 'undefined') {
      if (!_.some(this.rooms, (x) => x.id === id)) {
        this.handleStoreRooms('room', id);
        this.emitIntervalEvent('room', id);
      }
    }
  };

  leaveRoom = (id: any) => {
    if (id && id !== 'undefined') {
      if (this.isConnected) {
        this.socket?.emit('leaveRoom', id);
        _.remove(this.rooms, (x) => x.id === id);
      }
    }
  };

  /**
   * Joins an ECG event room, tagging the request with an optional preview flag. Emits joinEventRoom and records the room immediately when connected, otherwise queues the message and starts a one-second interval that flushes the whole queue once the connection is established.
   *
   * @param room - Identifier of the event room to join.
   * @param isPreview - Whether the join is for a preview session.
   * @returns Nothing.
   */
  sendJoinEventRoom(room: any, isPreview = false) {
    const msg = {
      room,
      isPreview,
    };
    this.queueRoomEvent.push(msg);
    if (this.isConnected) {
      this.socket?.emit('joinEventRoom', msg);
      this.handleStoreRooms('joinEventRoom', msg.room);
    } else if (!this.intervalJoinRoomEvent) {
      this.intervalJoinRoomEvent = setInterval(() => {
        if (this.isConnected) {
          const datas = _.assign([], this.queueRoomEvent);
          this.queueRoomEvent.splice(0, datas.length);
          _.forEach(datas, (e) => {
            this.socket?.emit('joinEventRoom', e);
            this.handleStoreRooms('joinEventRoom', e.room);
          });
          clearInterval(this.intervalJoinRoomEvent);
          this.intervalJoinRoomEvent = null;
        }
      }, 1000);
    }
  }

  /**
   * Joins a report room, tagging the request with an optional preview flag. Emits joinReportRoom and records the room immediately when connected, otherwise queues the message and starts a one-second interval that flushes the queue once connected, logging each emitted join.
   *
   * @param room - Identifier of the report room to join.
   * @param isPreview - Whether the join is for a preview session.
   * @returns Nothing.
   */
  sendJoinReportRoom(room: any, isPreview = false) {
    const msg = {
      room,
      isPreview,
    };
    this.queueRoomReport.push(msg);
    if (this.isConnected) {
      console.log('[ ==> ] sendJoinReportRoom connected', msg);
      this.socket?.emit('joinReportRoom', msg);
      this.handleStoreRooms('joinReportRoom', msg.room);
    } else if (!this.intervalJoinRoomReport) {
      this.intervalJoinRoomReport = setInterval(() => {
        if (this.isConnected) {
          const datas = _.assign([], this.queueRoomReport);
          this.queueRoomReport.splice(0, datas.length);
          _.forEach(datas, (e) => {
            console.log('[ ==> ] sendJoinReportRoom', e);
            this.socket?.emit('joinReportRoom', e);
            this.handleStoreRooms('joinReportRoom', e.room);
          });
          clearInterval(this.intervalJoinRoomReport);
          this.intervalJoinRoomReport = null;
        }
      }, 1000);
    }
  }

  disconnectSocket = ({ shouldExpireToken = false, shouldLogout = false } = {}) => {
    console.log('socket io disconnect', this.socket);
    if (this.socket) {
      this.socket.disconnect();
    }
    this.socket = null;
    this.rooms.length = 0;

    if (shouldExpireToken) {
      emitter.emit(EMITTER_CONSTANTS.EXPIRED_TOKEN);
      return;
    }

    if (shouldLogout) {
      emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.LOGOUT);
    }
  };
}

const staticSocket = new SocketClient();

export default staticSocket;
